/**
 * Safe Storage Utility
 * Handles localStorage with fallback to sessionStorage or memory storage
 * for incognito/private browsing mode compatibility
 */

class SafeStorage {
  constructor() {
    this.storageType = this.detectAvailableStorage();
    this.memoryStorage = {}; // Fallback if both localStorage and sessionStorage fail
  }

  detectAvailableStorage() {
    // Try localStorage first
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return 'localStorage';
    } catch (e) {
      console.warn('localStorage not available, trying sessionStorage');
    }

    // Try sessionStorage as fallback
    try {
      const testKey = '__storage_test__';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      return 'sessionStorage';
    } catch (e) {
      console.warn('sessionStorage not available, using memory storage');
    }

    // Use memory storage as last resort
    return 'memory';
  }

  getItem(key) {
    try {
      if (this.storageType === 'localStorage') {
        return localStorage.getItem(key);
      } else if (this.storageType === 'sessionStorage') {
        return sessionStorage.getItem(key);
      } else {
        return this.memoryStorage[key] || null;
      }
    } catch (e) {
      console.error('Error getting item from storage:', e);
      return null;
    }
  }

  setItem(key, value) {
    try {
      if (this.storageType === 'localStorage') {
        localStorage.setItem(key, value);
      } else if (this.storageType === 'sessionStorage') {
        sessionStorage.setItem(key, value);
      } else {
        this.memoryStorage[key] = value;
      }
      return true;
    } catch (e) {
      console.error('Error setting item in storage:', e);
      // Try fallback to memory storage
      this.memoryStorage[key] = value;
      return false;
    }
  }

  removeItem(key) {
    try {
      if (this.storageType === 'localStorage') {
        localStorage.removeItem(key);
      } else if (this.storageType === 'sessionStorage') {
        sessionStorage.removeItem(key);
      } else {
        delete this.memoryStorage[key];
      }
      return true;
    } catch (e) {
      console.error('Error removing item from storage:', e);
      delete this.memoryStorage[key];
      return false;
    }
  }

  clear() {
    try {
      if (this.storageType === 'localStorage') {
        localStorage.clear();
      } else if (this.storageType === 'sessionStorage') {
        sessionStorage.clear();
      } else {
        this.memoryStorage = {};
      }
      return true;
    } catch (e) {
      console.error('Error clearing storage:', e);
      this.memoryStorage = {};
      return false;
    }
  }

  getStorageType() {
    return this.storageType;
  }

  isIncognitoMode() {
    return this.storageType !== 'localStorage';
  }
}

// Export singleton instance
const storage = new SafeStorage();
export default storage;
